import {
  ArrowRight,
  BriefcaseBusiness,
  CalendarCheck2,
  CheckCircle2,
  KeyRound,
  MessageSquareText,
  Scale,
  Search,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import {
  signupPasswordPolicy,
  signupSchema,
  type PlanSummary,
  type SignupCompleteResult,
  type SignupStartResult,
} from '@cleopatra/contracts';
import { api } from './api';
import { useAuth } from './auth';
import { passkeysSupported } from './passkeys';

export function PublicHeader({
  compact = false,
  authenticated = false,
}: {
  compact?: boolean;
  authenticated?: boolean;
}) {
  return (
    <header className="public-header">
      <Link to="/" className="public-brand" aria-label="Cleopatra home">
        <span className="public-brand-mark">
          <Scale size={20} aria-hidden="true" />
        </span>
        <strong>Cleopatra</strong>
      </Link>
      {compact ? (
        <nav className="public-header-actions" aria-label="Account navigation">
          <Link to="/">Back to home</Link>
          <Link className="btn btn-outline-primary" to="/login">
            Log in
          </Link>
        </nav>
      ) : (
        <>
          <nav className="public-nav" aria-label="Main navigation">
            <a href="#platform">Platform</a>
            <a href="#organizations">Who it’s for</a>
            <a href="#security">Security</a>
          </nav>
          <nav className="public-header-actions" aria-label="Account navigation">
            {authenticated ? (
              <Link className="btn btn-primary" to="/app">
                Open workspace
              </Link>
            ) : (
              <>
                <Link to="/login">Log in</Link>
                <Link className="btn btn-primary" to="/signup">
                  Start free
                </Link>
              </>
            )}
          </nav>
        </>
      )}
    </header>
  );
}

export function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="public-site">
      <PublicHeader authenticated={Boolean(user)} />
      <main>
        <section className="public-hero">
          <div className="public-hero-copy">
            <div className="eyebrow">Legal work, clearly connected</div>
            <h1>Keep every matter moving in one calm workspace.</h1>
            <p>
              Cleopatra brings clients, deadlines, documents, tasks, and team activity together so
              legal professionals can see what matters now and what comes next.
            </p>
            <div className="public-actions" aria-live="polite">
              {user ? (
                <Link className="btn btn-primary btn-lg" to="/app">
                  Open your workspace <ArrowRight size={18} aria-hidden="true" />
                </Link>
              ) : (
                <>
                  <Link className="btn btn-primary btn-lg" to="/signup">
                    Start free <ArrowRight size={18} aria-hidden="true" />
                  </Link>
                  <Link className="btn btn-link btn-lg public-text-link" to="/login">
                    Log in to your workspace
                  </Link>
                </>
              )}
            </div>
            <div className="public-hero-note">
              <CheckCircle2 size={16} aria-hidden="true" /> No credit card required
            </div>
          </div>
          <aside className="public-product-card" aria-label="What Cleopatra brings together">
            <div className="public-product-card-header">
              <span className="public-card-kicker">Your day in Cleopatra</span>
              <span className="public-live-dot">Connected</span>
            </div>
            <h2>A complete view of the work</h2>
            <div className="public-work-list">
              <div>
                <span className="public-work-icon">
                  <BriefcaseBusiness size={18} aria-hidden="true" />
                </span>
                <span>
                  <strong>Matter context</strong>
                  <small>People, notes, documents, and time</small>
                </span>
              </div>
              <div>
                <span className="public-work-icon">
                  <CalendarCheck2 size={18} aria-hidden="true" />
                </span>
                <span>
                  <strong>Priorities and deadlines</strong>
                  <small>Clear ownership and next steps</small>
                </span>
              </div>
              <div>
                <span className="public-work-icon">
                  <MessageSquareText size={18} aria-hidden="true" />
                </span>
                <span>
                  <strong>Client communication</strong>
                  <small>Updates kept with the matter</small>
                </span>
              </div>
            </div>
            <div className="public-product-summary">
              <ShieldCheck size={18} aria-hidden="true" />
              <span>
                <strong>Built for accountable work</strong>
                <small>Permission-aware access and a clear activity history</small>
              </span>
            </div>
          </aside>
        </section>

        <section className="public-section" id="platform">
          <div className="public-section-heading">
            <div className="eyebrow">One connected platform</div>
            <h2>Spend less time piecing the work together.</h2>
            <p>
              Move from intake to active matter work with the information your team needs close at
              hand—not scattered across inboxes, calendars, and personal task lists.
            </p>
          </div>
          <div className="public-feature-grid">
            <article>
              <BriefcaseBusiness size={24} aria-hidden="true" />
              <h3>See the whole matter</h3>
              <p>
                Keep people, tasks, dates, notes, time, documents, and communication in context.
              </p>
            </article>
            <article>
              <CalendarCheck2 size={24} aria-hidden="true" />
              <h3>Know what needs attention</h3>
              <p>Give every next step an owner and keep upcoming and overdue work visible.</p>
            </article>
            <article>
              <MessageSquareText size={24} aria-hidden="true" />
              <h3>Keep clients in the loop</h3>
              <p>
                Carry intake details forward and preserve a useful history of client communication.
              </p>
            </article>
            <article>
              <Search size={24} aria-hidden="true" />
              <h3>Find work quickly</h3>
              <p>Search across matters, contacts, tasks, calendar events, documents, and intake.</p>
            </article>
          </div>
        </section>

        <section className="public-audience-section" id="organizations">
          <div className="public-audience-copy">
            <div className="eyebrow">Made for legal teams</div>
            <h2>Your operating model should shape the software—not the other way around.</h2>
            <p>
              Cleopatra supports the shared discipline of legal work while leaving room for how
              different organizations serve clients, assign responsibility, and measure progress.
            </p>
          </div>
          <div className="public-audience-cards">
            <article>
              <BriefcaseBusiness size={23} aria-hidden="true" />
              <h3>Private practices</h3>
              <p>
                Give solo and growing firms a clearer view of clients, matters, time, and
                follow-through.
              </p>
            </article>
            <article>
              <UsersRound size={23} aria-hidden="true" />
              <h3>Legal services organizations</h3>
              <p>
                Coordinate service delivery and matter work without forcing every outcome into
                billable hours.
              </p>
            </article>
          </div>
        </section>

        <section className="public-security-section" id="security">
          <div className="public-security-icon">
            <ShieldCheck size={30} aria-hidden="true" />
          </div>
          <div>
            <div className="eyebrow">Security with less friction</div>
            <h2>Keep sensitive work in the right hands.</h2>
            <p>
              Organization boundaries, permission-aware access, passkey sign-in, revocable sessions,
              and matter activity history are built into the way Cleopatra works.
            </p>
          </div>
          <Link className="btn btn-outline-light btn-lg" to="/login">
            Secure sign in <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </section>

        <section className="public-cta-section">
          <div>
            <div className="eyebrow">A clearer workday starts here</div>
            <h2>Bring your legal work into focus.</h2>
          </div>
          <Link className="btn btn-primary btn-lg" to={user ? '/app' : '/signup'}>
            {user ? 'Open your workspace' : 'Create your workspace'}
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </section>
      </main>
      <footer className="public-footer">
        <span>© 2026 Cleopatra</span>
        <span>Legal work, clearly connected</span>
      </footer>
    </div>
  );
}

export function SignupPage() {
  const { user, loading, signup, confirmSignup, setupPasskey, skipPasskeySetup } = useAuth();
  const navigate = useNavigate();
  const plans = useQuery({
    queryKey: ['commercial-plans'],
    queryFn: () => api<PlanSummary[]>('/plans'),
    staleTime: 5 * 60_000,
  });
  const [displayName, setDisplayName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [edition, setEdition] = useState<'firm' | 'mission'>('firm');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [registration, setRegistration] = useState<SignupStartResult | null>(null);
  const [completed, setCompleted] = useState<SignupCompleteResult | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user && !registration) return <Navigate to="/app" replace />;

  async function beginSignup(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const input = signupSchema.safeParse({
        displayName,
        organizationName,
        edition,
        email,
        password,
      });
      if (!input.success) {
        setError(input.error.issues[0]?.message ?? 'Check the signup details and try again.');
        return;
      }
      setRegistration(await signup(input.data));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to create the account.');
    } finally {
      setSubmitting(false);
    }
  }

  async function finishSignup(event: FormEvent) {
    event.preventDefault();
    if (!registration) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await confirmSignup({
        registrationId: registration.registrationId,
        confirmationCode,
        password,
      });
      if (result.passkeySetupAvailable && passkeysSupported()) {
        setCompleted(result);
        return;
      }
      await navigate('/app', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to confirm the account.');
    } finally {
      setSubmitting(false);
    }
  }

  async function addPasskey() {
    setSubmitting(true);
    setError('');
    try {
      await setupPasskey();
      await navigate('/app', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to create the passkey.');
    } finally {
      setSubmitting(false);
    }
  }

  async function skipPasskey() {
    setSubmitting(true);
    try {
      await skipPasskeySetup();
    } finally {
      await navigate('/app', { replace: true });
    }
  }

  const signupPlan = plans.data?.find((plan) => plan.selfService && plan.available);

  return (
    <div className="public-site">
      <PublicHeader compact />
      <main className="public-signup-layout">
        <section className="public-signup-copy">
          <div className="eyebrow">Start simply</div>
          <h1>A workspace for the work already on your desk.</h1>
          <p>
            Bring matters, deadlines, documents, and team follow-through into one clear view. Set up
            your organization and start with the essentials.
          </p>
          <div className="signup-plan-card">
            <div>
              <strong>{signupPlan?.name ?? 'Included tier'}</strong>
              <span>Included at signup</span>
            </div>
            <p>
              {signupPlan?.description ??
                'A focused legal-work workspace for managing matters and next steps.'}
            </p>
            <small>One user included. No credit card required.</small>
          </div>
          <ul className="signup-benefits">
            <li>
              <CheckCircle2 size={17} aria-hidden="true" /> Keep matter details in one place
            </li>
            <li>
              <CheckCircle2 size={17} aria-hidden="true" /> Track priorities and deadlines
            </li>
            <li>
              <CheckCircle2 size={17} aria-hidden="true" /> Sign in securely with a password or
              passkey
            </li>
          </ul>
        </section>

        {completed ? (
          <section className="public-signup-card" aria-labelledby="passkey-setup-title">
            <div className="eyebrow">Optional security</div>
            <h2 id="passkey-setup-title">Create a passkey?</h2>
            <p className="text-muted">
              Use Face ID, Touch ID, Windows Hello, or a security key for faster sign-in. Your
              password will still work.
            </p>
            {error && <div className="alert alert-danger">{error}</div>}
            <button
              className="btn btn-primary btn-lg w-100 mt-3 passkey-button"
              type="button"
              disabled={submitting}
              onClick={() => void addPasskey()}
            >
              <KeyRound size={18} aria-hidden="true" />
              {submitting ? 'Creating passkey…' : 'Create a passkey'}
            </button>
            <button
              className="btn btn-link w-100 mt-2"
              type="button"
              disabled={submitting}
              onClick={() => void skipPasskey()}
            >
              Skip for now
            </button>
          </section>
        ) : !registration ? (
          <form className="public-signup-card" onSubmit={(event) => void beginSignup(event)}>
            <div className="eyebrow">Create account</div>
            <h2>Set up your workspace</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <label className="form-label" htmlFor="signup-name">
              Your name
            </label>
            <input
              id="signup-name"
              className="form-control"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              autoComplete="name"
              required
            />
            <label className="form-label mt-3" htmlFor="signup-organization">
              Organization
            </label>
            <input
              id="signup-organization"
              className="form-control"
              value={organizationName}
              onChange={(event) => setOrganizationName(event.target.value)}
              autoComplete="organization"
              required
            />
            <fieldset className="signup-edition-fieldset mt-3">
              <legend className="form-label">Organization type</legend>
              <label>
                <input
                  type="radio"
                  name="edition"
                  checked={edition === 'firm'}
                  onChange={() => setEdition('firm')}
                />{' '}
                Private firm or solo practice
              </label>
              <label>
                <input
                  type="radio"
                  name="edition"
                  checked={edition === 'mission'}
                  onChange={() => setEdition('mission')}
                />{' '}
                Nonprofit or pro bono organization
              </label>
            </fieldset>
            <label className="form-label mt-3" htmlFor="signup-email">
              Email
            </label>
            <input
              id="signup-email"
              className="form-control"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              required
            />
            <label className="form-label mt-3" htmlFor="signup-password">
              Password
            </label>
            <input
              id="signup-password"
              className="form-control"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              minLength={signupPasswordPolicy.minLength}
              maxLength={signupPasswordPolicy.maxLength}
              title={signupPasswordPolicy.requirements}
              required
            />
            <small className="form-text">{signupPasswordPolicy.requirements}</small>
            <button className="btn btn-primary btn-lg w-100 mt-4" disabled={submitting}>
              {submitting ? 'Creating workspace…' : 'Create workspace'}
            </button>
            <p className="signup-login-link">
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </form>
        ) : (
          <form className="public-signup-card" onSubmit={(event) => void finishSignup(event)}>
            <div className="eyebrow">Verify email</div>
            <h2>Enter your confirmation code</h2>
            <p className="text-muted">
              We sent a code
              {registration.deliveryDestination ? ` to ${registration.deliveryDestination}` : ''}.
              Enter it below to finish setting up your workspace.
            </p>
            {error && <div className="alert alert-danger">{error}</div>}
            <label className="form-label" htmlFor="signup-code">
              Confirmation code
            </label>
            <input
              id="signup-code"
              className="form-control form-control-lg"
              value={confirmationCode}
              onChange={(event) => setConfirmationCode(event.target.value)}
              autoComplete="one-time-code"
              required
              autoFocus
            />
            <button className="btn btn-primary btn-lg w-100 mt-4" disabled={submitting}>
              {submitting ? 'Continuing…' : 'Confirm and continue'}
            </button>
            <button
              className="btn btn-link w-100 mt-2"
              type="button"
              onClick={() => {
                setRegistration(null);
                setConfirmationCode('');
                setError('');
              }}
              disabled={submitting}
            >
              Start over
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
